// HotX Unified Edge V1.7 - latest production source.
// Updated: 2026-09-09 04:52:24 CST.
// Parent baseline: 6d30cf4bfed3ee4a4a10bdc314dbb3106aa5d2cb.

import type { AccelerationRegion } from '../domain-entry/domainEntryForm'

export type ServiceType = 'DOMAIN' | 'PURE_IP'
export type FrontendProtocol = 'HTTP' | 'HTTPS' | 'TCP' | 'UDP'
export type DeploymentMode = 'SINGLE_NODE' | 'COMPONENT'

export interface UnifiedEdgeForm {
  serviceType: ServiceType
  name: string
  domainId?: number
  hostname: string
  deploymentMode: DeploymentMode
  accelerationRegion?: AccelerationRegion
  frontendProtocol: FrontendProtocol
  listenPort: number
  originProtocol: FrontendProtocol
  originAddress: string
  originPort: number
  backupEnabled: boolean
  backupAddress: string
  backupPort: number
  tcpIdleTimeoutSeconds: number
  tcpConnectTimeoutSeconds: number
  tcpHalfClose: boolean
  tcpMaxConnections?: number
  tcpCpsLimit?: number
  udpIdleTimeoutSeconds: number
  udpPpsLimit?: number
  udpBandwidthBps?: number
  udpMaxPacketBytes: number
  healthProbeType: string
  healthProbePayload: string
  healthProbeExpect: string
}

export interface ServiceView {
  id: number
  serviceType: ServiceType
  name: string
  deploymentMode: DeploymentMode
  lifecycleStatus: string
  rowVersion: number
  desiredVersion: number
  appliedVersion: number
  lastErrorCode?: string
  lastErrorMessage?: string
  endpoints: Array<{
    id: number
    frontend_protocol: FrontendProtocol
    listen_port: number
    hostname?: string
  }>
  assignments: Array<{
    id: number
    node_id: number
    lifecycle_status: string
    health_status: string
  }>
}

export interface OperationView {
  operationId: string
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED'
  resourceId: number
  errorCode?: string
  errorMessage?: string
}

export const protocolDefaults: Record<FrontendProtocol, number> = {
  HTTP: 80,
  HTTPS: 443,
  TCP: 10000,
  UDP: 10000,
}

export function isLayer4(protocol: FrontendProtocol): boolean {
  return protocol === 'TCP' || protocol === 'UDP'
}

export function normalizeOriginProtocol(frontend: FrontendProtocol, current: FrontendProtocol): FrontendProtocol {
  if (frontend === 'TCP' || frontend === 'UDP') return frontend
  return current === 'HTTP' || current === 'HTTPS' ? current : 'HTTP'
}

export function defaultHealthProbeType(frontend: FrontendProtocol): string {
  return isLayer4(frontend) ? 'NETWORK' : frontend
}

export function validateForm(form: UnifiedEdgeForm): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!form.name.trim()) errors.name = '请填写服务名称'
  // HotX V1.7 component-mode save/publish boundary.
  // Updated: 2026-09-09 CST; version: V1.7-mode-guidance; parent baseline: dbf4516c6af4426c457268848bf3cb5700883353.
  // COMPONENT is a valid persisted design mode. Production readiness is checked
  // by the official publish gate so the UI can save first and report the exact
  // publish result instead of replacing it with a generic client-side rejection.
  if (form.serviceType === 'DOMAIN' && !form.hostname.trim()) errors.hostname = '请填写域名（Host/SNI）'
  if (form.listenPort < 1 || form.listenPort > 65535) errors.listenPort = '端口必须在 1 至 65535 之间'
  if (isLayer4(form.frontendProtocol)) {
    const reserved = form.frontendProtocol === 'TCP' ? [22, 80, 443] : [51820]
    if (reserved.includes(form.listenPort)) errors.listenPort = '该端口为节点管理或共享服务保留端口'
    if (form.listenPort >= 8451 && form.listenPort <= 8457) errors.listenPort = '8451 至 8457 为节点现有服务保留端口'
  }
  if (!form.originAddress.trim()) errors.originAddress = '请填写主源站地址'
  if (form.originPort < 1 || form.originPort > 65535) errors.originPort = '源站端口必须在 1 至 65535 之间'
  if (form.backupEnabled && !form.backupAddress.trim()) errors.backupAddress = '请填写备用源站地址'
  if (form.backupEnabled && (form.backupPort < 1 || form.backupPort > 65535)) errors.backupPort = '备用源站端口无效'
  if (form.udpMaxPacketBytes < 512 || form.udpMaxPacketBytes > 65507) errors.udpMaxPacketBytes = 'UDP 报文必须在 512 至 65507 字节之间'
  return errors
}

export function buildCreatePayload(form: UnifiedEdgeForm) {
  const origins = [
    {
      role: 'PRIMARY',
      protocol: form.originProtocol,
      address: form.originAddress.trim(),
      port: form.originPort,
      weight: form.backupEnabled ? 80 : 100,
      priority: 0,
    },
  ]
  if (form.backupEnabled) {
    origins.push({
      role: 'BACKUP',
      protocol: form.originProtocol,
      address: form.backupAddress.trim(),
      port: form.backupPort,
      weight: 20,
      priority: 10,
    })
  }
  return {
    serviceType: form.serviceType,
    name: form.name.trim(),
    domainId: form.serviceType === 'DOMAIN' ? form.domainId : undefined,
    deploymentMode: form.deploymentMode,
    accelerationRegion: form.accelerationRegion || 'APAC',
    endpoint: {
      frontendProtocol: form.frontendProtocol,
      listenPort: form.listenPort,
      hostname: form.serviceType === 'DOMAIN' ? form.hostname.trim() : undefined,
      certificateMode: form.frontendProtocol === 'HTTPS'
        ? (form.serviceType === 'PURE_IP' ? 'NODE_IP_CERT' : 'DOMAIN_CERT')
        : 'NONE',
      tcpIdleTimeoutSeconds: form.tcpIdleTimeoutSeconds,
      tcpConnectTimeoutSeconds: form.tcpConnectTimeoutSeconds,
      tcpHalfClose: form.tcpHalfClose,
      tcpMaxConnections: form.tcpMaxConnections,
      tcpCpsLimit: form.tcpCpsLimit,
      udpIdleTimeoutSeconds: form.udpIdleTimeoutSeconds,
      udpPpsLimit: form.udpPpsLimit,
      udpBandwidthBps: form.udpBandwidthBps,
      udpMaxPacketBytes: form.udpMaxPacketBytes,
      healthProbeType: form.healthProbeType,
      healthProbePayload: form.healthProbePayload || undefined,
      healthProbeExpect: form.healthProbeExpect || undefined,
      origins,
    },
  }
}

// HotX Unified Edge V1.7 mature-flow integration.
// Updated: 2026-09-09 CST; version: V1.7-flow-integration; parent baseline: c65f36872ccf8abca719303cc1bc7f76eb55d823.
// Keep the released single-endpoint builder above for compatibility. New UI
// flows submit every selected protocol as one logical service and one quota item.
export type MultiEndpointCreatePayload = Omit<ReturnType<typeof buildCreatePayload>, 'endpoint'> & {
  endpoints: Array<ReturnType<typeof buildCreatePayload>['endpoint']>
}

export function buildCreatePayloads(forms: UnifiedEdgeForm[]): MultiEndpointCreatePayload {
  if (!forms.length) throw new Error('请至少选择一个加速协议')
  for (const form of forms) {
    const errors = validateForm(form)
    const firstError = Object.values(errors)[0]
    if (firstError) throw new Error(firstError)
  }
  const payloads = forms.map(form => buildCreatePayload(form))
  const first = payloads[0]
  const seen = new Set<string>()
  for (const payload of payloads) {
    if (payload.serviceType !== first.serviceType
      || payload.name !== first.name
      || payload.domainId !== first.domainId
      || payload.deploymentMode !== first.deploymentMode) {
      throw new Error('同一服务的类型、名称、域名和节点模式必须一致')
    }
    if (payload.accelerationRegion !== first.accelerationRegion) {
      throw new Error('同一服务的加速区域必须一致')
    }
    const key = `${payload.endpoint.frontendProtocol}:${payload.endpoint.listenPort}`
    if (seen.has(key)) throw new Error('同一服务不能重复配置相同协议和监听端口')
    seen.add(key)
  }
  return {
    serviceType: first.serviceType,
    name: first.name,
    domainId: first.domainId,
    deploymentMode: first.deploymentMode,
    accelerationRegion: first.accelerationRegion,
    endpoints: payloads.map(payload => payload.endpoint),
  }
}
